/**
 * The only module in the app that performs HTTP.
 *
 * Owns the base URL, the auth header, the single 401 refresh-and-retry, and
 * turning every failure into a typed ApiError. Feature code never constructs a
 * request, so these concerns are configured once instead of remembered at each
 * call site (docs/16 section 8.2).
 */
import { ApiError, kindForStatus, messageFromPayload } from './errors'
import { accessTokenExpired, clearSession, getAccessToken, getRefreshToken, setSession } from './session'
import type { PublicConfig } from './types'

// `??` only catches null/undefined, so an env var that is set but EMPTY used to
// produce an empty base and strip the /api/v1 prefix off every request. Treat
// blank as absent; the build guard in vite.config.ts refuses it out loud as well.
const API_BASE: string = (import.meta.env.VITE_API_BASE || '').trim() || '/api/v1'

let publicConfig: PublicConfig | null = null
let onSessionLost: (() => void) | null = null

/** Called once at boot; also used by the auth layer to reach Supabase. */
export async function loadPublicConfig(): Promise<PublicConfig> {
  if (publicConfig) return publicConfig
  const res = await fetch(`${API_BASE}/public/config`)
  if (!res.ok) {
    throw new ApiError('server', 'Could not read the server configuration.', res.status)
  }
  publicConfig = (await res.json()) as PublicConfig
  return publicConfig
}

export function getPublicConfig(): PublicConfig | null {
  return publicConfig
}

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler
}

function supabaseBase(): string {
  return (publicConfig?.supabase_url ?? '').replace(/\/$/, '')
}

/**
 * Exchange the refresh token for a new access token. Returns false when the
 * session is genuinely over, which is the only case that should reach the user.
 */
export async function refreshSession(): Promise<boolean> {
  const refresh = getRefreshToken()
  const base = supabaseBase()
  if (!refresh || !base) return false
  try {
    const res = await fetch(`${base}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: publicConfig?.supabase_anon_key ?? '',
      },
      body: JSON.stringify({ refresh_token: refresh }),
    })
    if (!res.ok) return false
    const json = (await res.json()) as {
      access_token?: string
      refresh_token?: string
      expires_in?: number
    }
    if (!json.access_token) return false
    setSession(json.access_token, json.refresh_token ?? refresh, json.expires_in ?? 3600)
    return true
  } catch (error) {
    // Network failure during refresh is not an expired session. Say so.
    console.warn('Session refresh could not reach the auth server.', error)
    return false
  }
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function request<T>(path: string, init: RequestInit, retried = false): Promise<T> {
  if (!retried && accessTokenExpired() && getRefreshToken()) {
    await refreshSession()
  }

  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  const token = getAccessToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  } catch (error) {
    throw new ApiError('offline', 'Could not reach YourDealPulse.', null, error)
  }

  if (res.status === 401 && !retried) {
    if (await refreshSession()) return request<T>(path, init, true)
    clearSession()
    onSessionLost?.()
    throw new ApiError('unauthenticated', 'Your session has expired.', 401)
  }

  const payload = await parseBody(res)

  if (!res.ok) {
    throw new ApiError(
      kindForStatus(res.status),
      messageFromPayload(payload, res.statusText || 'Request failed'),
      res.status,
      payload,
    )
  }

  return payload as T
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' })
}

/**
 * Writes are never retried automatically (docs/16 section 8.3). A duplicated
 * analysis costs the user a credit; an error message costs them a click.
 */
export function apiPost<T>(path: string, body?: FormData | unknown): Promise<T> {
  const payload = body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body)
  return request<T>(path, { method: 'POST', body: payload as BodyInit | undefined })
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
}

/** The endpoint answers 204, so there is nothing to hand back. */
export async function apiDelete(path: string): Promise<void> {
  await request<unknown>(path, { method: 'DELETE' })
}

/**
 * A file download that carries the session.
 *
 * `request` parses every response as text or JSON, which would corrupt a CSV
 * and lose the filename the server chose. This performs the same auth dance —
 * refresh a stale token, retry once on 401 — and hands back the bytes plus the
 * name from Content-Disposition, so the server names the file rather than the
 * browser guessing.
 */
export async function apiDownload(path: string, retried = false): Promise<{ blob: Blob; filename: string }> {
  if (!retried && accessTokenExpired() && getRefreshToken()) {
    await refreshSession()
  }
  const headers = new Headers()
  const token = getAccessToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, { method: 'GET', headers })
  } catch (error) {
    throw new ApiError('offline', 'Could not reach YourDealPulse.', null, error)
  }

  if (res.status === 401 && !retried) {
    if (await refreshSession()) return apiDownload(path, true)
    clearSession()
    onSessionLost?.()
    throw new ApiError('unauthenticated', 'Your session has expired.', 401)
  }
  if (!res.ok) {
    const payload = await parseBody(res)
    throw new ApiError(kindForStatus(res.status), messageFromPayload(payload, 'Export failed'), res.status, payload)
  }

  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^";]+)"?/.exec(disposition)
  return { blob: await res.blob(), filename: match?.[1] ?? 'dealpulse-export.csv' }
}
